import { Button, Flex, Headline, Icon, Row, Tooltip } from '@upsales/components';
import { InlineDeleteButton } from '../../ClientCard/SimpleListView/SimpleListView';
import T, { useTranslation } from 'Components/Helpers/translate';
import Attribute, { Type } from 'App/babel/attributes/Attribute';
import OrderAttributes from 'App/babel/attributes/Order';
import ComparisonTypes from 'Resources/ComparisonTypes';
import RequestBuilder from 'Resources/RequestBuilder';
import { useSelector } from 'App/components/hooks';
import SimpleListView from '../../ClientCard/SimpleListView';
import OrderResource from 'App/resources/Order';
import Order from 'App/resources/Model/Order';
import openModal from 'App/services/Modal';
import moment from 'moment';
import { openEditOrder } from 'App/components/EditOrder';
import React from 'react';
import { CardEntity } from './types';

const COLUMNS = ['date', 'description', 'contact', 'value', 'stage', 'user', 'clientConnection', 'tools'];
const PROJECT_COLUMNS = [
	'date',
	'description',
	'contact',
	'client',
	'value',
	'stage',
	'user',
	'clientConnection',
	'tools'
];

const getData = (
	rb: RequestBuilder,
	cardEntity: CardEntity
): Promise<{ data: Order[]; metadata: { total: number } }> => {
	rb.addFilter(OrderAttributes.probability, ComparisonTypes.NotEquals, 100);

	if (cardEntity.type === 'client') {
		const clientOr = rb.orBuilder();
		clientOr.next();
		clientOr.addFilter(OrderAttributes.account, ComparisonTypes.Equals, cardEntity.id);
		clientOr.next();
		clientOr.addFilter(OrderAttributes.clientConnection, ComparisonTypes.Equals, cardEntity.id);
		clientOr.done();
	} else if (cardEntity.type === 'project') {
		rb.addFilter(OrderAttributes.project, ComparisonTypes.Equals, cardEntity.id);
	} else {
		rb.addFilter({ field: 'contact.id' }, ComparisonTypes.Equals, cardEntity.id);
	}

	return OrderResource.find(rb.build());
};

const generateSubHeader = (item: Order, sorting: string) => {
	switch (sorting) {
		case 'date':
			return moment(item.date).year().toString();
		case 'user.name':
			return item.user.name;
		case 'stage.id':
			return item.stage.name;
	}
	return '';
};

const onRowClick =
	({ id }: Order) =>
	() => {
		openEditOrder({ id, type: 'opportunity' });
	};

const onDelete = (item: Order) => {
	return OrderResource.delete(item.id);
};

const ClientConnection = ({ item }: { item: Order }) => {
	const clientConnectionName = useSelector(({ App }) => App.metadata?.params.clientOrderRelation || '');

	if (!item.clientConnection || item.clientConnection.id !== item.client.id) {
		return null;
	}

	return (
		<Tooltip title={`${item.client.name} ${T('default.is').toLowerCase()} ${clientConnectionName.toLowerCase()}`}>
			<Icon name="sitemap" />
		</Tooltip>
	);
};

const CreateDocumentButton = ({ item }: { item: Order }) => {
	const templates = useSelector(({ App }) => App.documentTemplates['order'] || []);

	return (
		<Tooltip title={T('default.createDocument')} position="left">
			<Icon
				color="grey-10"
				space="plm prm ptm pbm"
				name="file-o"
				onClick={e => {
					e.stopPropagation();
					openModal('CreateDocumentModal', {
						type: 'order',
						id: item.id,
						accountId: item.client.id,
						templates
					});
				}}
			/>
		</Tooltip>
	);
};

const MoveFromCampaignButton = ({ item, onMove }: { item: Order; onMove: () => void }) => {
	if (!item.userEditable) {
		return null;
	}

	return (
		<Flex flex={[0, 0, '40px']} justifyContent="center">
			<Tooltip title={T('default.removeFromCampaign')} position="left">
				<Button
					type="link"
					color="grey"
					onClick={e => {
						e.stopPropagation();
						onMove();
					}}
				>
					<Icon name="times-circle" />
				</Button>
			</Tooltip>
		</Flex>
	);
};

const getCustomRenders = (
	cardEntity: CardEntity,
	onMutate: () => void
): GetProp<typeof SimpleListView<Order>, 'customColumnRenders'> => ({
	clientConnection: item => <ClientConnection item={item} />,
	tools: (item, provided) => (
		<Flex justifyContent="end" alignItems="center">
			<CreateDocumentButton item={item} />
			{cardEntity.type === 'project' ? (
				<MoveFromCampaignButton
					item={item}
					onMove={() =>
						OrderResource.save({ id: item.id, project: null }).then(() => {
							onMutate();
							provided.reloadTable({ silent: true });
						})
					}
				/>
			) : (
				<InlineDeleteButton
					deleteFn={onDelete}
					entityName={'default.opportunity'}
					isDeletable={() => true}
					item={item}
					provided={provided}
				/>
			)}
		</Flex>
	)
});

const attributes = {
	...OrderAttributes,
	contact: {
		...OrderAttributes.contact,
		placeholder: 'default.noContact'
	},
	clientConnection: Attribute({
		title: '',
		type: Type.String,
		field: ''
	}),
	tools: Attribute({
		title: '',
		type: Type.String,
		field: ''
	})
};

const SharedOpportunities = ({
	cardEntity,
	onMutate,
	hideTitle,
	hideGroupings,
	tableLimitOptions = [25]
}: {
	cardEntity: CardEntity;
	onMutate?: () => void;
	hideTitle?: boolean;
	hideGroupings?: boolean;
	tableLimitOptions?: number[] & { 0: number };
}) => {
	const { t } = useTranslation();
	return (
		<SimpleListView<Order>
			getData={(rb: RequestBuilder) => getData(rb, cardEntity)}
			columns={cardEntity.type === 'project' ? PROJECT_COLUMNS : COLUMNS}
			subHeaderFn={hideGroupings ? undefined : generateSubHeader}
			renderHeader={
				hideTitle
					? () => <></>
					: () => (
							<Row align="center" className="SimpleListView__header">
								<Headline space="plm" size="sm">
									{t('default.opportunities')}
								</Headline>
								<div className="FlexPadder" />
							</Row>
					  )
			}
			attributes={attributes}
			rowStyles={{
				inactive: item => item.probability === 0
			}}
			customColumnRenders={getCustomRenders(cardEntity, onMutate ?? (() => {}))}
			onRowClick={onRowClick}
			broadcastType={['order', 'opportunity']}
			initialSorting={[{ attribute: 'date', ascending: false }]}
			tableLimitOptions={tableLimitOptions}
		/>
	);
};

export default SharedOpportunities;
